import dayjs from 'dayjs';
import { EYapePromotionCodeStatus } from 'core/model/enums/yape.enum';
import {
  ICreateYapePromotionRequest,
  IEditYapePromotionRequest,
  IEditYapeQrRequest,
  IYapePromotionListParams,
  IYapePromotionListResponse,
  IYapePromotionReportParams,
  IYapePromotionReportResponse,
  IYapeQrListParams,
  IYapeQrListResponse,
} from 'core/model/interfaces/yape/yape.interface';
import { formatDateToDayAndShortMonth, formatLocaleDateTime } from 'shared/utils/date.utils';
import { buildFormData } from 'shared/utils/object.utils';
import {
  ICreateYapePromotionRequestDTO,
  IEditYapePromotionRequestDTO,
  IEditYapeQrRequestDTO,
  IYapePromotionListParamsDTO,
  IYapePromotionListResponseDTO,
  IYapePromotionReportParamsDTO,
  IYapePromotionReportResponseDTO,
  IYapeQrListParamsDTO,
  IYapeQrListResponseDTO,
} from './yape.dto';

export const mapYapePromotionListDtoToDomain = ({
  yapePromotions,
  pagination,
}: IYapePromotionListResponseDTO): IYapePromotionListResponse => ({
  yapePromotions: yapePromotions.map(({ limitDate, startDate, endDate, imageUrl, ...rest }) => {
    const startDateFormatted = formatLocaleDateTime(dayjs().toISOString());
    const endDateFormatted = formatLocaleDateTime(dayjs(endDate).endOf('day').toISOString());

    return {
      ...rest,
      limitDateFormatted: limitDate && `Limite de canje: ${formatDateToDayAndShortMonth(limitDate)}`,
      validity:
        startDate &&
        endDate &&
        `Vigencia:
        ${formatDateToDayAndShortMonth(startDate)} al ${formatDateToDayAndShortMonth(endDate)}`,
      startDate,
      endDate,
      limitDate,
      imageUrl,
      isExpired: dayjs(startDateFormatted).isAfter(dayjs(endDateFormatted)),
    };
  }),
  pagination,
});

export const mapYapePromotionListParamsDomainToDto = ({
  ...rest
}: IYapePromotionListParams): IYapePromotionListParamsDTO => ({
  ...rest,
});

export const mapCreateYapePromotionRequestDomainToDto = ({
  imageFile,
  ...rest
}: ICreateYapePromotionRequest): FormData => {
  const body: ICreateYapePromotionRequestDTO = {
    ...rest,
    imageFile: imageFile?.blob,
  };
  return buildFormData(body);
};

export const mapEditYapePromotionRequestDomainToDto = ({
  imageFile,
  id,
  ...rest
}: IEditYapePromotionRequest): FormData => {
  const body: IEditYapePromotionRequestDTO = {
    ...rest,
    imageFile: imageFile?.blob,
  };
  return buildFormData(body);
};

export const mapYapeQrListDtoToDomain = ({ codes, pagination }: IYapeQrListResponseDTO): IYapeQrListResponse => ({
  codes: codes.map(({ externalCustomerId, isActive, redeemedAt, ...rest }) => {
    let status;
    if (isActive) {
      status = redeemedAt ? EYapePromotionCodeStatus.REDEEMED : EYapePromotionCodeStatus.UNREDEEMED;
    } else {
      status = EYapePromotionCodeStatus.INACTIVE;
    }

    return {
      ...rest,
      externalCustomerId: externalCustomerId ? `Bod. ${externalCustomerId}` : '\u00A0',
      status,
      redeemedAt: redeemedAt && `Canjeado: ${formatDateToDayAndShortMonth(redeemedAt)}`,
      isActive,
    };
  }),
  pagination,
});

export const mapYapeQrListParamsDomainToDto = ({ id, ...rest }: IYapeQrListParams): IYapeQrListParamsDTO => ({
  ...rest,
});

export const mapEditYapeQrRequestDomainToDto = ({
  yapePromoId,
  yapeQrId,
  ...rest
}: IEditYapeQrRequest): IEditYapeQrRequestDTO => ({
  ...rest,
});

export const mapYapePromotionReportParamsDomainToDto = ({
  id,
  ...rest
}: IYapePromotionReportParams): IYapePromotionReportParamsDTO => ({
  ...rest,
});

export const mapYapePromotionReportResponseDtoToDomain = ({
  returnForTheWarehouseKeeper,
  yapePromotionInformation,
}: IYapePromotionReportResponseDTO): IYapePromotionReportResponse => {
  let mappedData = returnForTheWarehouseKeeper;

  if (mappedData)
    mappedData = {
      ...mappedData,
      invalidAndObservedCodes:
        returnForTheWarehouseKeeper?.invalidAndObservedCodes.map(({ status, ...rest }) => ({
          status: mapYapePromotionStatus[status.toUpperCase()],
          ...rest,
        })) ?? [],
    };

  return { returnForTheWarehouseKeeper: mappedData, yapePromotionInformation };
};

export const mapYapePromotionStatus = {
  [EYapePromotionCodeStatus.OBSERVED]: 'Observado',
  [EYapePromotionCodeStatus.INACTIVE]: 'Inactivo',
};
